import { AddressDisplay } from '../../components/address-display';
import { AddressForm } from '../../components/address-form';
import {
  Typography,
  Select,
  LinkButton,
  Button,
  Checkbox,
  ButtonVariants,
} from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  useAddressForm,
  useGetAddressAlertMessage,
} from '../../hooks/address-form.hook';
import { IndicatorIcon } from '../../icons/icon-def/IndicatorIcon';
import { testId } from '../../utils';
import { CheckoutAddressStepProps } from './checkout-address';
import { CheckedState } from '@radix-ui/react-checkbox';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';
import { z } from 'zod';

const InlineAddress: React.FC<{
  address: Cornerstone.ValueAddress;
}> = ({ address }) => {
  return (
    <Typography variant="body">{`${address.addressLine1} ${address.city}, ${address.regionIsoCode} ${address.postalCode}`}</Typography>
  );
};

interface EditAddressProps
  extends Omit<CheckoutAddressStepProps, 'addAddressForm' | 'editAddressForm'> {
  address: Cornerstone.ValueAddress;
  onCancel: () => void;
  addressForm: ReturnType<typeof useAddressForm>;
}

export const EditAddress: React.FC<EditAddressProps> = ({
  'data-testid': dataTestId,
  address,
  onContinue,
  onCancel,
  noDefault,
  addressForm,
  checkoutLoading,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });
  const getAddressAlertMessage = useGetAddressAlertMessage();

  const { form, regionQuery, countries, countriesLoading, AddressSchema } =
    addressForm;
  const addressServerError = form.formState.errors.root?.serverError;

  React.useEffect(() => {
    if (countries && address) {
      form.reset(address);
    }
  }, [address, countries, form]);

  type AddressSchema = z.infer<typeof AddressSchema>;

  const updateAddressMutation = Cornerstone.useUpdateAddress({
    mutation: {
      onSuccess: (response) => {
        onContinue?.(response.data);
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to update address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });

        const errorMessage = getAddressAlertMessage(error);

        if (!errorMessage) return;

        form.setError('root.serverError', {
          message: errorMessage,
        });
      },
    },
  });

  const { regions, loading: regionsLoading, label: regionLabel } = regionQuery;
  const { handleSubmit } = form;

  const onSubmit = React.useCallback(
    (data: AddressSchema) => {
      updateAddressMutation.mutate({ addressId: address.id!, data });
    },
    [address.id, updateAddressMutation],
  );

  return (
    <div {...testId(dataTestId)} className="flex flex-col gap-7">
      <RequiredFieldIndicator />
      {addressServerError && (
        <Alert {...testId(dataTestId, 'alert')} variant="error">
          {addressServerError.message}
        </Alert>
      )}
      <AddressForm
        loading={
          updateAddressMutation.isLoading || countriesLoading || regionsLoading
        }
        countries={countries}
        regions={regions}
        regionLabel={regionLabel}
        form={form}
        onSubmit={handleSubmit(onSubmit)}
        noDefault={noDefault}
      >
        <div className="flex flex-col md:flex-row gap-4">
          {onContinue && (
            <Button
              {...testId(dataTestId, 'continue')}
              size="sm"
              type="submit"
              onClick={handleSubmit(onSubmit)}
              loading={updateAddressMutation.isLoading}
              disabled={updateAddressMutation.isLoading || checkoutLoading}
            >
              {t('auth.continue')}
            </Button>
          )}
          <Button
            {...testId(dataTestId, 'cancel')}
            variant={ButtonVariants.Secondary}
            size="sm"
            type="button"
            onClick={onCancel}
            disabled={updateAddressMutation.isLoading}
          >
            {t('user.settings.account.cancel_button')}
          </Button>
        </div>
      </AddressForm>
    </div>
  );
};

type AddressSelectItem = {
  address?: Cornerstone.ValueAddress;
  label: React.ReactNode;
  value: string;
};

type AddressSelectProps = {
  'data-testid': string;
  selectedAddress?: Cornerstone.ValueAddress;
  addressBook: Cornerstone.AddressBookResponse;
  onSelect: (address?: Cornerstone.ValueAddress) => void;
  onAddNewAddress: () => void;
};

const AddressSelect: React.FC<AddressSelectProps> = ({
  'data-testid': dataTestId,
  selectedAddress,
  addressBook,
  onSelect,
  onAddNewAddress,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });
  const addressData: AddressSelectItem[] = React.useMemo(() => {
    const { defaultShipping, additional } = addressBook;

    const additionalAddressData = additional
      ? additional.map((address) => ({
          address,
          label: <InlineAddress address={address} />,
          value: address.id!.toString(),
        }))
      : [];

    const addNewAddress: AddressSelectItem = {
      label: t('checkout.add_new_address'),
      value: '-1',
    };

    if (defaultShipping) {
      return [
        {
          address: defaultShipping,
          label: <InlineAddress address={defaultShipping} />,
          value: defaultShipping.id!.toString(),
        },
        ...additionalAddressData,
        addNewAddress,
      ];
    }

    return [...additionalAddressData, addNewAddress];
  }, [addressBook, t]);

  const onChange = React.useCallback(
    (addressId: string) => {
      const addressItem = addressData.find(
        (address) => address.value === addressId,
      );

      if (!addressItem?.address && addressItem?.value === '-1') {
        // adding a new address
        onAddNewAddress();
        return;
      }

      onSelect(addressItem?.address);
    },
    [addressData, onSelect, onAddNewAddress],
  );

  return (
    <div className="flex flex-col gap-1">
      <Typography variant="form-label">
        {t('user.addresses.address')} <IndicatorIcon />
      </Typography>
      <Select
        className="w-full"
        {...testId(dataTestId)}
        placeholder={t('checkout.select_an_option')!}
        value={selectedAddress?.id?.toString()}
        data={addressData}
        onChange={onChange}
      />
    </div>
  );
};

const SaveAsDefault: React.FC<{
  'data-testid': string;
  address: Cornerstone.ValueAddress;
  checked: CheckedState;
  onChange: (checked: CheckedState) => void;
}> = ({ 'data-testid': dataTestId, address, checked, onChange }) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'route.user.addresses',
  });

  if (address.defaultShipping) {
    return;
  }

  return (
    <div className="flex items-center gap-1">
      <Checkbox
        {...testId(dataTestId)}
        id="default-shipping"
        checked={checked}
        onCheckedChange={onChange}
      />
      <Typography variant="body">{t('set_default')}</Typography>
    </div>
  );
};

interface ExistingAddressProps
  extends Omit<CheckoutAddressStepProps, 'addAddressForm' | 'editAddressForm'> {
  addressForm: ReturnType<typeof useAddressForm>;
  address?: Cornerstone.ValueAddress;
  addressBook: Cornerstone.AddressBookResponse;
  onAddNewAddress: () => void;
}

export const ExistingAddress: React.FC<ExistingAddressProps> = ({
  'data-testid': dataTestId,
  address,
  addressBook,
  onContinue,
  onSelect,
  onAddNewAddress,
  noDefault,
  addressForm,
  onEdit,
  checkoutLoading,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });
  const [saveAsPrimary, setSaveAsPrimary] = React.useState(false);
  const [editingAddress, setEditingAddress] = React.useState(false);
  const [replacingAddress, setReplacingAddress] = React.useState(
    noDefault ||
      (!addressBook.defaultShipping &&
        addressBook.additional &&
        addressBook.additional.length > 1),
  );
  const [selectedAddress, setSelectedAddress] = React.useState<
    Cornerstone.ValueAddress | undefined
  >(
    noDefault
      ? undefined
      : address ||
          addressBook.defaultShipping ||
          (addressBook.additional && addressBook.additional.length === 1
            ? addressBook.additional[0]
            : undefined),
  );

  const onReplaceAddress = React.useCallback(() => {
    setReplacingAddress(true);
  }, []);

  const onEditAddress = React.useCallback(() => {
    onEdit?.();
    setEditingAddress(true);
  }, [onEdit]);

  const onCancelEditing = React.useCallback(() => {
    setEditingAddress(false);
  }, []);

  const onContinueEditing = React.useCallback(
    (address: Cornerstone.ValueAddress) => {
      onContinue?.(address);
      setEditingAddress(false);
    },
    [onContinue],
  );

  const onSelectAddress = React.useCallback(
    (address?: Cornerstone.ValueAddress) => {
      setSelectedAddress(address);

      if (address) {
        onSelect?.(address);
        setReplacingAddress(false);
      }
    },
    [onSelect],
  );

  const defaultShippingMutation = Cornerstone.useUpdateAddress({
    mutation: {
      onSuccess: (response) => {
        onContinue?.(response.data);
      },
      onError: (error?: any) => {
        console.error({
          title: 'Failed to update address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const onContinueWithAddress = React.useCallback(() => {
    if (saveAsPrimary) {
      defaultShippingMutation.mutate({
        addressId: selectedAddress!.id!,
        data: { ...selectedAddress!, defaultShipping: true },
      });

      return;
    }

    onContinue?.(selectedAddress!);
  }, [saveAsPrimary, onContinue, selectedAddress, defaultShippingMutation]);

  if (editingAddress) {
    return (
      <EditAddress
        {...testId(dataTestId, 'edit-address-form')}
        address={selectedAddress!}
        onContinue={onContinueEditing}
        onCancel={onCancelEditing}
        noDefault={noDefault}
        addressForm={addressForm}
        checkoutLoading={checkoutLoading}
      />
    );
  }

  return (
    <div
      {...testId(dataTestId)}
      className="pt-4 flex flex-col gap-6 items-start"
    >
      <div className="w-full flex flex-col gap-4">
        {selectedAddress && (
          <AddressDisplay
            {...testId(dataTestId, 'selected-address')}
            address={selectedAddress}
          />
        )}
        {selectedAddress && (
          <LinkButton
            {...testId(dataTestId, 'edit-address')}
            onClick={onEditAddress}
          >
            {t('user.settings.account.edit_button')}
          </LinkButton>
        )}
        {replacingAddress && (
          <AddressSelect
            {...testId(dataTestId, 'address-select')}
            selectedAddress={selectedAddress}
            addressBook={addressBook}
            onSelect={onSelectAddress}
            onAddNewAddress={onAddNewAddress}
          />
        )}
        {!replacingAddress && (
          <LinkButton
            {...testId(dataTestId, 'replace-address')}
            onClick={onReplaceAddress}
          >
            {t('checkout.replace_address')}
          </LinkButton>
        )}
        {selectedAddress && !noDefault && (
          <SaveAsDefault
            {...testId(dataTestId, 'default-shipping')}
            address={selectedAddress}
            checked={saveAsPrimary}
            onChange={(checked) => setSaveAsPrimary(checked === true)}
          />
        )}
      </div>
      {onContinue && (
        <Button
          {...testId(dataTestId, 'continue')}
          disabled={!selectedAddress || checkoutLoading}
          size="sm"
          onClick={onContinueWithAddress}
        >
          {t('auth.continue')}
        </Button>
      )}
    </div>
  );
};
